import type { UnknownQueryParam, Url } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { RawAccountTypeDetailsMap } from "~/areas/infrastructure/InfrastructureDetails";
import { client } from "~/clientInstance";
import type { UrlResolver } from "~/components/Navigation/useUrlResolver";
import type { PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { PageRegistration } from "~/routing/pageRegistrations/PageRegistration";
import { allPages } from "~/routing/pageRegistrations/allPageRegistrations";
export interface GlobalSearchPageRegistration {
    Id(urlResolver: UrlResolver): string;
    name: string;
    area: string;
    areaAndKeywords: string;
    isAuthorized(): boolean;
    resolvedUrl(urlResolver: UrlResolver): string;
}
export function createGlobalSearchPageRegistration<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData, LoaderContext, PageContext>({ RouteLink, pageRegistration, customLabel, }: {
    pageRegistration: PageRegistration<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext>;
    customLabel?: string;
    RouteLink: () => Url;
    RoutePermission?: PermissionCheckProps;
}): GlobalSearchPageRegistration {
    return {
        Id(urlResolver: UrlResolver) {
            // Assume links are unique across global search. Every page we link to should have a single entry in global search.
            return urlResolver.resolve(RouteLink());
        },
        areaAndKeywords: pageRegistration.searchKeywords.length > 0 ? `${pageRegistration.pageArea} (${pageRegistration.searchKeywords.join(", ")})` : pageRegistration.pageArea,
        area: pageRegistration.pageArea,
        name: customLabel ?? pageRegistration.pageName,
        resolvedUrl(urlResolver: UrlResolver) {
            return urlResolver.resolve(RouteLink());
        },
        isAuthorized() {
            return pageRegistration.requiredPermission === undefined || isAllowed(pageRegistration.requiredPermission);
        },
    };
}
const dashboardPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.pages.dashboardConfigurationPage,
        RouteLink: () => links.dashboardConfigurationPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
];
const libraryPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.feedsPage,
        RouteLink: () => links.feedsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createFeedPage,
        RouteLink: () => links.createFeedPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.stepTemplatesPage,
        RouteLink: () => links.stepTemplatesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.builtInStepTemplatesPage,
        RouteLink: () => links.builtInStepTemplatesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.communityStepTemplatesPage,
        RouteLink: () => links.communityStepTemplatesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.lifecyclesPage,
        RouteLink: () => links.lifecyclesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createLifecyclePage,
        RouteLink: () => links.createLifecyclePage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.certificatesPage,
        RouteLink: () => links.certificatesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createCertificatePage,
        RouteLink: () => links.createCertificatePage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.variableSetsPage,
        RouteLink: () => links.variableSetsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.tagSetsPage,
        RouteLink: () => links.tagSetsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createTagSetPage,
        RouteLink: () => links.createTagSetPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.builtInRepositoryPage,
        RouteLink: () => links.builtInRepositoryPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.buildInformationOverviewPage,
        RouteLink: () => links.buildInformationOverviewPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.scriptModulesPage,
        RouteLink: () => links.scriptModulesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createScriptModulePage,
        RouteLink: () => links.createScriptModulePage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.gitCredentialsPage,
        RouteLink: () => links.gitCredentialsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.library.pages.createGitCredentialPage,
        RouteLink: () => links.createGitCredentialPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
];
const configurationPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.userInvitesPage,
        RouteLink: () => links.userInvitesPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.auditPage,
        RouteLink: () => links.auditPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.auditArchivePage,
        RouteLink: () => links.auditArchivePage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.backupPage,
        RouteLink: () => links.backupPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.featuresPage,
        RouteLink: () => links.featuresPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.licensePage,
        RouteLink: () => links.licensePage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.licenseUsagePage,
        RouteLink: () => links.licenseUsagePage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.smtpPage,
        RouteLink: () => links.smtpPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.nodesPage,
        RouteLink: () => links.nodesPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.serverSettingsPage,
        RouteLink: () => links.serverSettingsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.thumbprintPage,
        RouteLink: () => links.thumbprintPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.maintenancePage,
        RouteLink: () => links.maintenancePage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.letsEncryptPage,
        RouteLink: () => links.letsEncryptPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.configureLetsEncryptPage,
        RouteLink: () => links.configureLetsEncryptPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.testPermissionsPage,
        RouteLink: () => links.testPermissionsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.subscriptionsPage,
        RouteLink: () => links.subscriptionsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.createSubscriptionPage,
        RouteLink: () => links.createSubscriptionPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.usersPage,
        RouteLink: () => links.usersPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.createUserPage,
        RouteLink: () => links.createUserPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.spacesPage,
        RouteLink: () => links.spacesPage.generateUrl(),
    }),
    // Excluding creating spaces as an edge case - our UI creates these via a popover.
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.teamsPage,
        RouteLink: () => links.teamsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.userRolesPage,
        RouteLink: () => links.userRolesPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.createUserRolePage,
        RouteLink: () => links.createUserRolePage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticsPage,
        RouteLink: () => links.diagnosticsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticLogsPage,
        RouteLink: () => links.diagnosticLogsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticAutoDeployLogsPage,
        RouteLink: () => links.diagnosticAutoDeployLogsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticScheduledDeployLogsPage,
        RouteLink: () => links.diagnosticScheduledDeployLogsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.diagnosticSubscriptionLogsPage,
        RouteLink: () => links.diagnosticSubscriptionLogsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.childGroups.settings.pages.settingsListPage,
        RouteLink: () => links.settingsListPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.performancePage,
        RouteLink: () => links.performancePage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.telemetryPage,
        RouteLink: () => links.telemetryPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.configuration.pages.deploymentFreezesListPage,
        RouteLink: () => links.deploymentFreezesListPage.generateUrl(),
    }),
];
const taskPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.pages.tasksPage,
        RouteLink: () => links.tasksPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.pages.scriptConsolePage,
        RouteLink: () => links.scriptConsolePage.generateUrl(),
    }),
];
const infrastructurePages: () => GlobalSearchPageRegistration[] = () => {
    const results: GlobalSearchPageRegistration[] = [
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.infrastructureOverviewPage,
            RouteLink: () => links.infrastructureOverviewPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.infrastructureEnvironmentsPage,
            RouteLink: () => links.infrastructureEnvironmentsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        // Excluding creating environments as an edge case - our UI creates these via a popover.
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.deploymentTargetsPage,
            RouteLink: () => links.deploymentTargetsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.newDeploymentTargetPage,
            RouteLink: () => links.newDeploymentTargetPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.proxiesPage,
            RouteLink: () => links.proxiesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.createProxyPage,
            RouteLink: () => links.createProxyPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.machinePoliciesPage,
            RouteLink: () => links.machinePoliciesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.createMachinePolicyPage,
            RouteLink: () => links.createMachinePolicyPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.infrastructureAccountsPage,
            RouteLink: () => links.infrastructureAccountsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        // Excluding creating accounts as an edge case - our UI creates these via a popover.
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.workerMachinesPage,
            RouteLink: () => links.workerMachinesPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.newWorkerMachinePage,
            RouteLink: () => links.newWorkerMachinePage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.workerPoolsPage,
            RouteLink: () => links.workerPoolsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        // Excluding creating worker pools as an edge case - our UI creates these via a popover.
    ];
    // Inject our account types dynamically.
    const accountTypeDetailsMap = RawAccountTypeDetailsMap;
    accountTypeDetailsMap.forEach((accountTypeDetails) => {
        if (accountTypeDetails.types.length === 0) {
            return null;
        }
        results.push(createGlobalSearchPageRegistration({
            customLabel: `Add New ${accountTypeDetails.name} ${accountTypeDetails.name.endsWith("Account") ? "" : "Account"}`,
            pageRegistration: allPages.childGroups.space.childGroups.infrastructure.pages.createInfrastructureAccountPage,
            RouteLink: () => links.createInfrastructureAccountPage.generateUrl({ spaceId: client.spaceId ?? "" }, { accountType: accountTypeDetails.types[0] }),
        }));
    });
    return results;
};
const projectPages = (): GlobalSearchPageRegistration[] => {
    return [
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.pages.projectsPage,
            RouteLink: () => links.projectsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.childGroups.importExports.pages.importExportProjectTasksPage,
            RouteLink: () => links.importExportProjectTasksPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.childGroups.importExports.pages.importProjectsPage,
            RouteLink: () => links.importProjectsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
        createGlobalSearchPageRegistration({
            pageRegistration: allPages.childGroups.space.childGroups.projects.childGroups.importExports.pages.exportProjectsPage,
            RouteLink: () => links.exportProjectsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
        }),
    ];
};
const tenantPages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.space.childGroups.tenants.pages.tenantsPage,
        RouteLink: () => links.tenantsPage.generateUrl({ spaceId: client.spaceId ?? "" }),
    }),
];
const userProfilePages: GlobalSearchPageRegistration[] = [
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.currentUser.pages.currentUserLoginsPage,
        RouteLink: () => links.currentUserLoginsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.currentUser.pages.currentUserDetailsPage,
        RouteLink: () => links.currentUserDetailsPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.currentUser.pages.currentUserApiKeysPage,
        RouteLink: () => links.currentUserApiKeysPage.generateUrl(),
    }),
    createGlobalSearchPageRegistration({
        pageRegistration: allPages.childGroups.system.childGroups.currentUser.pages.currentUserActivityPage,
        RouteLink: () => links.currentUserActivityPage.generateUrl(),
    }),
];
export const pagesToIncludeInSearch = () => {
    return [...dashboardPages, ...libraryPages, ...configurationPages, ...taskPages, ...infrastructurePages(), ...projectPages(), ...tenantPages, ...userProfilePages];
};
